/**
 * Валидация и санитизация загружаемых картинок.
 *
 * Поддержка: SVG, PNG, JPEG, WebP. Лимит 512 КБ и 1024×1024.
 * SVG очищается от скриптов, обработчиков и внешних ссылок.
 */

export const MAX_IMAGE_BYTES = 512 * 1024
export const MAX_IMAGE_DIMENSION = 1024

export const SUPPORTED_IMAGE_TYPES = [
  'image/svg+xml',
  'image/png',
  'image/jpeg',
  'image/webp'
] as const

export type SupportedImageType = (typeof SUPPORTED_IMAGE_TYPES)[number]

export interface ValidatedImage {
  blob: Blob
  mime: SupportedImageType
}

export type ImageValidationResult =
  | { ok: true; image: ValidatedImage }
  | { ok: false; error: string }

/** Убрать из SVG исполняемые и внешние конструкции. */
export function sanitizeSvg(source: string): string {
  return source
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|xlink:href)\s*=\s*("|')\s*javascript:[^"']*\2/gi, '')
    .replace(/(href|xlink:href)\s*=\s*("|')\s*https?:[^"']*\2/gi, '')
}

function isSupportedType(value: string): value is SupportedImageType {
  return (SUPPORTED_IMAGE_TYPES as readonly string[]).includes(value)
}

function readImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Не удалось прочитать изображение'))
    }
    image.src = url
  })
}

/** Проверить файл и вернуть нормализованный Blob. */
export async function validateImageFile(
  file: File
): Promise<ImageValidationResult> {
  if (!isSupportedType(file.type)) {
    return {
      ok: false,
      error: 'Поддерживаются SVG, PNG, JPEG и WebP'
    }
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return { ok: false, error: 'Файл больше 512 КБ' }
  }

  if (file.type === 'image/svg+xml') {
    const text = await file.text()
    const clean = sanitizeSvg(text).trim()
    if (!clean.toLowerCase().startsWith('<svg')) {
      return { ok: false, error: 'Файл не похож на SVG' }
    }
    return {
      ok: true,
      image: { blob: new Blob([clean], { type: file.type }), mime: file.type }
    }
  }

  try {
    const { width, height } = await readImageDimensions(file)
    if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
      return {
        ok: false,
        error: `Максимальный размер ${MAX_IMAGE_DIMENSION}×${MAX_IMAGE_DIMENSION}`
      }
    }
  } catch (err: unknown) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Не удалось прочитать изображение'
    }
  }

  return { ok: true, image: { blob: file, mime: file.type } }
}

/** Blob → base64 без префикса data URL. */
export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}
